import {
  ActivityDirection,
  AI_RELATIONS,
  AiFileActivityId,
  INFO_CATEGORIES,
  InfoCategoryId,
  LevelBreakdown,
  RiskCube,
  direction_of,
  empty_levels,
  empty_risk_cube,
  resolve_relation,
} from "./model";
import { LevelKey } from "./virtual-labels";

/**
 * Rows from the Gen AI queries, folded into what the cards draw. Pure, so the
 * spec's row shapes can be tested without the app around them.
 */

/** a content label below one of the six categories, with its own grade */
export interface GradedLabel {
  category: InfoCategoryId;
  level: LevelKey;
  name: string;
}

const empty_by_direction = (): Record<ActivityDirection, LevelBreakdown> => ({
  employee: empty_levels(),
  ai: empty_levels(),
});

/** ObjectSeverity (the spec) names its levels; Severity (QA today) numbers them */
const SEVERITY_LEVEL: Record<string, LevelKey> = {
  high: "high",
  medium: "medium",
  low: "low",
  "3": "high",
  "2": "medium",
  "1": "low",
};

/* ------------------------------------------------------------------ *
 * W1 and W2
 * ------------------------------------------------------------------ */

export interface RiskFold {
  /** category x activity x severity */
  cube: RiskCube;
  /** each direction's risky activity by severity, for the share line */
  risky: Record<ActivityDirection, LevelBreakdown>;
  /** relation codes outside the relation model (RELATION_CODE), left uncounted */
  unknown_relations: string[];
  /** severities other than High, Medium and Low */
  unknown_severities: string[];
}

/**
 * `category_of` maps a cell's label — one of the six categories' L0 ids, as
 * the risk queries ask (data/categories.ts) — to its row. A category label is
 * not cumulative the way the virtual labels are, so its counts add up as they
 * come.
 */
export const fold_risk_cells = (
  cells: readonly { relation: string; label: string; severity: string; count: number }[],
  category_of: Map<string, InfoCategoryId>
): RiskFold => {
  const cube = empty_risk_cube();
  const risky = empty_by_direction();
  const unknown_relations = new Set<string>();
  const unknown_severities = new Set<string>();

  cells.forEach((cell) => {
    const category = category_of.get(cell.label);
    if (!category) return;
    const activity = resolve_relation(cell.relation);
    if (!activity) {
      unknown_relations.add(cell.relation);
      return;
    }
    const severity = SEVERITY_LEVEL[String(cell.severity || "").toLowerCase()];
    if (!severity) {
      unknown_severities.add(cell.severity);
      return;
    }
    cube[category][activity][severity] += cell.count;
    risky[direction_of(activity)][severity] += cell.count;
  });

  return {
    cube,
    risky,
    unknown_relations: [...unknown_relations],
    unknown_severities: [...unknown_severities],
  };
};

/* ------------------------------------------------------------------ *
 * W3 and W4
 * ------------------------------------------------------------------ */

/** each category's AI activity by relation, at one slider stop */
export type StopCounts = Record<InfoCategoryId, Record<AiFileActivityId, number>>;

export const empty_stop_counts = (): StopCounts =>
  INFO_CATEGORIES.reduce((acc, c) => {
    acc[c.id] = AI_RELATIONS.reduce((rel, r) => {
      rel[r.id] = 0;
      return rel;
    }, {} as Record<AiFileActivityId, number>);
    return acc;
  }, {} as StopCounts);

export interface VisibilityFold {
  counts: StopCounts;
  /** each direction's activity across the six categories */
  activity: Record<ActivityDirection, number>;
  unknown_relations: string[];
}

/**
 * The Visibility cards' rows at one slider stop. The query asked for one
 * label per category — the category itself, or its virtual label at that
 * stop — so a row's count is the category's own, and they add up as they
 * come. `category_of` names a label's row.
 */
export const fold_visibility_rows = (
  rows: readonly { relation: string; label: string; count: number }[],
  category_of: (label: string) => InfoCategoryId | undefined
): VisibilityFold => {
  const counts = empty_stop_counts();
  const activity: Record<ActivityDirection, number> = { employee: 0, ai: 0 };
  const unknown_relations = new Set<string>();
  rows.forEach((row) => {
    const category = category_of(row.label);
    if (!category) return;
    const id = resolve_relation(row.relation);
    if (!id) {
      unknown_relations.add(row.relation);
      return;
    }
    counts[category][id] += row.count;
    activity[direction_of(id)] += row.count;
  });
  return { counts, activity, unknown_relations: [...unknown_relations] };
};

/* ------------------------------------------------------------------ *
 * W5
 * ------------------------------------------------------------------ */

export interface PeopleFold {
  /** employees per category at one slider stop, every employee type summed */
  rows: Record<InfoCategoryId, number>;
  /**
   * The headline: the largest category. The counts are per category and
   * cannot be deduplicated across them, so this is the fewest people it can
   * be, not a sum of the rows.
   */
  total: number;
}

/**
 * Who interacted with AI at one slider stop, from intel-api's aggregated
 * counts: each cell is the employees of one type under one label (the
 * category's, or its virtual label at that stop).
 */
export const fold_people = (
  cells: readonly { label: string; count: number }[],
  category_of: (label: string) => InfoCategoryId | undefined
): PeopleFold => {
  const rows = INFO_CATEGORIES.reduce((acc, c) => {
    acc[c.id] = 0;
    return acc;
  }, {} as Record<InfoCategoryId, number>);
  cells.forEach((cell) => {
    const category = category_of(cell.label);
    if (category) rows[category] += cell.count;
  });
  return { rows, total: Math.max(0, ...INFO_CATEGORIES.map((c) => rows[c.id])) };
};

/* ------------------------------------------------------------------ *
 * W3's expand
 * ------------------------------------------------------------------ */

/** each category's sub-labels by how much employees shared to AI under them */
export const to_label_breakdown = (
  rows: readonly { relation: string; label: string; count: number }[],
  graded: Map<string, GradedLabel>
) => {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const id = resolve_relation(row.relation);
    if (!id || direction_of(id) !== "employee" || !graded.has(row.label)) return;
    counts.set(row.label, (counts.get(row.label) || 0) + row.count);
  });
  return INFO_CATEGORIES.map((c) => ({
    id: c.id,
    name: c.name,
    icon: c.icon,
    items: [...counts.entries()]
      .filter(([id]) => (graded.get(id) as GradedLabel).category === c.id)
      .map(([id, count]) => ({
        id,
        name: (graded.get(id) as GradedLabel).name,
        count,
        category: c.id,
      }))
      .sort((a, b) => b.count - a.count),
    ms_coverage: null,
  }));
};
